import { Store } from 'pullstate';
import type firebase from 'firebase';

import { db } from './firebase';
import { updateGameplayStore } from './gameplayStore';

import { getRandomName } from '../functions/generateCharacter';

interface Heartstring {
  name: string;
  value: number;
}

interface CustomMove {
  title: string;
  type: 'custom';
  description: string;
}

interface Character {
  name: string;
  pronouns?: string;
  xp?: number;
  conditions?: {[name: string]: boolean};
  stats?: {
    daring?: number;
    grace?: number,
    heart?: number,
    wit?: number,
    spirit?: number,
    [name: string]: any
  },
  strings?: Heartstring[];
  playbook?: string,
  moves?: string[];
  customMoves?: {[name: string]: CustomMove};
}

const getInitialCharacterState = (character: Character) => {
  return {...character};
};

interface AppState {
  isSubscribed: boolean;
  isLoading?: boolean;
  error?: any;
  unsubscribe: () => void;
}


export const AppStateStore = new Store<AppState>({
  isSubscribed: false,
  unsubscribe: () => {},
});

export const CharacterStore = new Store<Character>({ name: getRandomName() });

// update custom sections of GameplayStore
CharacterStore.createReaction(
  s => ({
    playbook: s.playbook,
    moveKeys: s.moves,
    conditions: s.conditions,
    customMoves: s.customMoves,
  }),
  s => {
    updateGameplayStore(s);
  }
);

export function subscribe(channel: string, character: string, create: boolean = false) {
  AppStateStore.update(s => {
    s.unsubscribe();
  })
  const subscriptions: (() => void)[] = [];

  if (!channel || !character) {
    return;
  }

  AppStateStore.update(s => {
    s.isSubscribed = false;
    s.isLoading = true;
    s.error = undefined;
  });

  // update character ref
  // query for a character of that name
  db.ref(channel)
    .child('characters')
    .orderByChild('name')
    .equalTo(character)
    .once('value')
    .then(function (snapshot) {
      let characterRef: firebase.database.Reference | null = null;

      if (snapshot.exists()) {
        console.log('Character exists');
        snapshot.forEach(function (childSnapshot) {
          characterRef = childSnapshot.ref;
        });
        AppStateStore.update(s => {
          s.isSubscribed = true;
        });
      } else if (create) {
        console.log('Creating new character');
        // if character with name does not exist, create a new one
        characterRef = db.ref(channel).child('characters').push().ref;
        characterRef.set(getInitialCharacterState({name: character}));
        AppStateStore.update(s => {
          s.isSubscribed = true;
        });
      } else {
        throw new Error(`Unable to find ${character} in channel ${channel}`);
      }
      /*// purely for logging:
      characterRef.once('value').then(function (snapshot) {
        console.log(snapshot.val());
      });*/

      // subscribe to firebase store
      characterRef?.on('value', function (snapshot) {
        if (snapshot.exists()) {
          CharacterStore.replace(snapshot.val());
          //console.log('firebase update');
          //console.log(snapshot.val());
        } else {
          // character has been deleted, reset data
          characterRef?.set(
            CharacterStore.update(s => {
              getInitialCharacterState(s)
            })

          );
        }

        // sync with firebase
        subscriptions.push(
          CharacterStore.createReaction(
            s => s,
            (s, draft) => {
              if (!characterRef) {
                return;
              }
              characterRef.update(draft);
            }
          )
        );
      });

      subscriptions.push(() => characterRef ? characterRef.off(): {});
    })
    .catch(e => {
      AppStateStore.update(s => {
        s.error = e.message;
      });
    })
    .finally(() => {
      AppStateStore.update(s => {
        s.isLoading = false;
      });
    });

  const unsubscribe = () => subscriptions.forEach(unsub => unsub());
  AppStateStore.update(s => {
    s.unsubscribe = unsubscribe;
  });

  return unsubscribe;
}
